import { todayMx } from '../lib/dateUtils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Search, X, TrendingUp, TrendingDown, DollarSign, Users, Edit, Trash2, BarChart3 } from 'lucide-react';

const ESTATUS = [
  { value: 'pendiente', label: 'Pendiente', cls: 'bg-amber-200 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200' },
  { value: 'en_proceso', label: 'En proceso', cls: 'bg-blue-200 text-blue-900 dark:bg-blue-900/50 dark:text-blue-200' },
  { value: 'finalizada', label: 'Finalizada', cls: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200' },
  { value: 'cancelada', label: 'Cancelada', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
];
const estByVal = Object.fromEntries(ESTATUS.map(e => [e.value, e]));
const CATEGORIAS_GASTO = ['renta','internet','luz','agua','publicidad','software','telefono','seguro','otro'];

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function emptyOrder() {
  return { fecha: todayMx(), cliente_nombre: '', cliente_telefono: '', cliente_direccion: '', tecnico_id: '', tecnico_name: '', tipo_servicio: '', trabajo: '', total_cobrado: 0, anticipo: 0, material_utilizado: '', gastos_material: 0, gasolina: 0, otros_gastos: 0, observaciones: '', estatus: 'pendiente', folio_nota: '', forma_pago: '' };
}

export default function Finance() {
  const { user } = useAuth();
  const [tab, setTab] = useState('ordenes');
  const [orders, setOrders] = useState([]);
  const [fixed, setFixed] = useState([]);
  const [report, setReport] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  // Order filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterTec, setFilterTec] = useState('all');
  const [filterEst, setFilterEst] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Report date range
  const [reportFrom, setReportFrom] = useState(() => { const t = todayMx(); return t.slice(0, 8) + '01'; });
  const [reportTo, setReportTo] = useState(todayMx());
  const [reportTec, setReportTec] = useState('all');

  // Dialogs
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderForm, setOrderForm] = useState(emptyOrder());
  const [editOrder, setEditOrder] = useState(null);
  const [fixedOpen, setFixedOpen] = useState(false);
  const [fixedForm, setFixedForm] = useState({ concepto: '', categoria: 'renta', monto_mensual: 0, fecha_inicio: todayMx(), activo: true, observaciones: '' });
  const [editFixed, setEditFixed] = useState(null);

  const isAdmin = user && user.role === 'admin';

  const fetchOrders = useCallback(async () => {
    if (!isAdmin) return;
    const params = new URLSearchParams();
    if (filterSearch) params.append('search', filterSearch);
    if (filterTec !== 'all') params.append('tecnico', filterTec);
    if (filterEst !== 'all') params.append('estatus', filterEst);
    if (filterFrom) params.append('fecha_from', filterFrom);
    if (filterTo) params.append('fecha_to', filterTo);
    try { const r = await axios.get(`${API}/finance/orders?${params}`); setOrders(r.data); } catch { toast.error('Error órdenes'); }
  }, [isAdmin, filterSearch, filterTec, filterEst, filterFrom, filterTo]);

  const fetchFixed = useCallback(async () => {
    if (!isAdmin) return;
    try { const r = await axios.get(`${API}/finance/fixed-expenses`); setFixed(r.data); } catch { toast.error('Error gastos fijos'); }
  }, [isAdmin]);

  const fetchReport = useCallback(async () => {
    if (!isAdmin) return;
    if (!reportFrom || !reportTo) return;
    const params = new URLSearchParams({ period: 'custom', fecha_from: reportFrom, fecha_to: reportTo });
    if (reportTec !== 'all') params.append('tecnico', reportTec);
    try { const r = await axios.get(`${API}/finance/reports?${params}`); setReport(r.data); } catch { toast.error('Error reporte'); }
  }, [isAdmin, reportFrom, reportTo, reportTec]);

  const fetchTechs = useCallback(async () => {
    if (!isAdmin) return;
    try { const r = await axios.get(`${API}/technicians`); setTechnicians(r.data.filter(t => t.is_active !== false)); } catch (e) { /* ignore */ }
  }, [isAdmin]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { fetchTechs(); }, [fetchTechs]);
  useEffect(() => { if (tab === 'ordenes') fetchOrders(); }, [tab, fetchOrders]);
  useEffect(() => { if (tab === 'fijos') fetchFixed(); }, [tab, fetchFixed]);
  useEffect(() => { if (tab === 'reportes') fetchReport(); }, [tab, fetchReport]);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (user && !isAdmin) return <Navigate to="/clients" replace />;

  // --- Orders handlers ---
  const openNewOrder = () => { setEditOrder(null); setOrderForm(emptyOrder()); setOrderOpen(true); };
  const openEditOrder = (o) => { setEditOrder(o); setOrderForm({ ...emptyOrder(), ...o }); setOrderOpen(true); };
  const computeOrder = (f) => {
    const t = parseFloat(f.total_cobrado) || 0, a = parseFloat(f.anticipo) || 0;
    const gm = parseFloat(f.gastos_material) || 0, gg = parseFloat(f.gasolina) || 0, go = parseFloat(f.otros_gastos) || 0;
    return { saldo: t - a, gastos: gm + gg + go, utilidad: t - (gm + gg + go) };
  };
  const submitOrder = async (e) => {
    e.preventDefault();
    try {
      if (editOrder) await axios.put(`${API}/finance/orders/${editOrder.id}`, orderForm);
      else await axios.post(`${API}/finance/orders`, orderForm);
      toast.success(editOrder ? 'Orden actualizada' : 'Orden creada');
      setOrderOpen(false);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const deleteOrder = async (o) => {
    if (!window.confirm(`¿Eliminar orden ${o.folio}?`)) return;
    try { await axios.delete(`${API}/finance/orders/${o.id}`); toast.success('Eliminada'); fetchOrders(); } catch { toast.error('Error'); }
  };

  // --- Fixed expenses handlers ---
  const openNewFixed = () => { setEditFixed(null); setFixedForm({ concepto: '', categoria: 'renta', monto_mensual: 0, fecha_inicio: todayMx(), activo: true, observaciones: '' }); setFixedOpen(true); };
  const openEditFixed = (f) => { setEditFixed(f); setFixedForm({ ...f }); setFixedOpen(true); };
  const submitFixed = async (e) => {
    e.preventDefault();
    try {
      if (editFixed) await axios.put(`${API}/finance/fixed-expenses/${editFixed.id}`, fixedForm);
      else await axios.post(`${API}/finance/fixed-expenses`, fixedForm);
      toast.success('Guardado'); setFixedOpen(false); fetchFixed();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const deleteFixed = async (f) => {
    if (!window.confirm(`¿Eliminar gasto "${f.concepto}"?`)) return;
    try { await axios.delete(`${API}/finance/fixed-expenses/${f.id}`); toast.success('Eliminado'); fetchFixed(); } catch { toast.error('Error'); }
  };

  const setOrderTec = (id) => {
    const t = technicians.find(x => x.id === id);
    setOrderForm({ ...orderForm, tecnico_id: id, tecnico_name: t?.name || '' });
  };

  const setRangePreset = (preset) => {
    const t = todayMx(); // YYYY-MM-DD en zona local México
    const [y, m, d] = t.split('-').map(Number);
    const pad = (n) => String(n).padStart(2, '0');
    const toStr = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    const today = new Date(y, m - 1, d);
    if (preset === 'hoy') {
      setReportFrom(t); setReportTo(t);
    } else if (preset === 'semana') {
      // Semana REPACS: viernes -> jueves
      const daysSinceFri = (today.getDay() - 5 + 7) % 7; // Vie=5
      const fri = new Date(y, m - 1, d - daysSinceFri);
      const thu = new Date(fri.getFullYear(), fri.getMonth(), fri.getDate() + 6);
      setReportFrom(toStr(fri)); setReportTo(toStr(thu));
    } else if (preset === 'mes') {
      const first = new Date(y, m - 1, 1);
      const last = new Date(y, m, 0); // último día del mes
      setReportFrom(toStr(first)); setReportTo(toStr(last));
    } else if (preset === 'anio') {
      setReportFrom(`${y}-01-01`); setReportTo(`${y}-12-31`);
    }
  };

  const c = computeOrder(orderForm);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="finance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="h-6 w-6 text-purple-600" />Control Financiero</h2>
          <div className="text-muted-foreground text-sm flex items-center gap-1">Órdenes de trabajo, gastos y reportes. <Badge variant="destructive" className="ml-1 text-xs">Solo Admin</Badge></div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ordenes" data-testid="fin-tab-ordenes">Órdenes</TabsTrigger>
          <TabsTrigger value="fijos" data-testid="fin-tab-fijos">Gastos Fijos</TabsTrigger>
          <TabsTrigger value="reportes" data-testid="fin-tab-reportes">Reportes</TabsTrigger>
        </TabsList>

        {/* === ORDENES === */}
        <TabsContent value="ordenes" className="space-y-3">
          <div className="flex justify-end"><Button onClick={openNewOrder} data-testid="fin-new-order"><Plus className="h-4 w-4 mr-1" />Nueva orden</Button></div>
          <Card><CardContent className="p-3 flex flex-wrap gap-2 items-end">
            <div className="relative flex-1 min-w-[180px]"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" /><Input placeholder="Buscar folio, cliente, trabajo..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} className="pl-8" /></div>
            <Select value={filterTec} onValueChange={setFilterTec}><SelectTrigger className="w-[160px]"><SelectValue placeholder="Técnico" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los técnicos</SelectItem>{technicians.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterEst} onValueChange={setFilterEst}><SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{ESTATUS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="w-[140px]" />
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="w-[140px]" />
            {(filterSearch || filterTec !== 'all' || filterEst !== 'all' || filterFrom || filterTo) && <Button variant="ghost" size="sm" onClick={() => { setFilterSearch(''); setFilterTec('all'); setFilterEst('all'); setFilterFrom(''); setFilterTo(''); }}><X className="h-4 w-4 mr-1" />Limpiar</Button>}
          </CardContent></Card>

          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60"><tr className="text-xs">
                <th className="text-left px-2 py-2">Folio</th>
                <th className="text-left px-2 py-2">Fecha</th>
                <th className="text-left px-2 py-2">Cliente</th>
                <th className="text-left px-2 py-2">Técnico</th>
                <th className="text-left px-2 py-2">Trabajo</th>
                <th className="text-right px-2 py-2">Cobrado</th>
                <th className="text-right px-2 py-2">Gastos</th>
                <th className="text-right px-2 py-2">Utilidad</th>
                <th className="text-left px-2 py-2">Estatus</th>
                <th className="px-2 py-2"></th>
              </tr></thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Sin órdenes</td></tr>
                ) : orders.map(o => (
                  <tr key={o.id} className="border-t hover:bg-muted/30">
                    <td className="px-2 py-1.5 font-mono text-xs">{o.folio}</td>
                    <td className="px-2 py-1.5 text-xs">{o.fecha}</td>
                    <td className="px-2 py-1.5 text-xs">{o.cliente_nombre}</td>
                    <td className="px-2 py-1.5 text-xs">{o.tecnico_name || '-'}</td>
                    <td className="px-2 py-1.5 text-xs max-w-[180px] truncate" title={o.trabajo}>{o.trabajo}</td>
                    <td className="px-2 py-1.5 text-right text-xs font-medium">{fmt(o.total_cobrado)}</td>
                    <td className="px-2 py-1.5 text-right text-xs text-red-600">{fmt(o.total_gastos)}</td>
                    <td className={`px-2 py-1.5 text-right text-xs font-bold ${o.utilidad_neta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(o.utilidad_neta)}</td>
                    <td className="px-2 py-1.5"><span className={`text-xs px-2 py-0.5 rounded ${estByVal[o.estatus]?.cls || ''}`}>{estByVal[o.estatus]?.label || o.estatus}</span></td>
                    <td className="px-2 py-1.5"><div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditOrder(o)}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteOrder(o)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        {/* === GASTOS FIJOS === */}
        <TabsContent value="fijos" className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground">Total mensual activo: <span className="font-bold text-red-600">{fmt(fixed.filter(f => f.activo).reduce((s,f) => s + (parseFloat(f.monto_mensual)||0), 0))}</span></p>
            <Button onClick={openNewFixed} data-testid="fin-new-fixed"><Plus className="h-4 w-4 mr-1" />Nuevo gasto fijo</Button>
          </div>
          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60"><tr className="text-xs">
                <th className="text-left px-2 py-2">Concepto</th>
                <th className="text-left px-2 py-2">Categoría</th>
                <th className="text-right px-2 py-2">Mensual</th>
                <th className="text-left px-2 py-2">Desde</th>
                <th className="text-left px-2 py-2">Activo</th>
                <th className="px-2 py-2"></th>
              </tr></thead>
              <tbody>
                {fixed.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Sin gastos fijos</td></tr> :
                  fixed.map(f => (
                    <tr key={f.id} className="border-t hover:bg-muted/30">
                      <td className="px-2 py-1.5">{f.concepto}</td>
                      <td className="px-2 py-1.5 text-xs capitalize">{f.categoria}</td>
                      <td className="px-2 py-1.5 text-right font-medium">{fmt(f.monto_mensual)}</td>
                      <td className="px-2 py-1.5 text-xs">{f.fecha_inicio}</td>
                      <td className="px-2 py-1.5">{f.activo ? <Badge className="bg-emerald-600 text-xs">Activo</Badge> : <Badge variant="outline" className="text-xs">Pausado</Badge>}</td>
                      <td className="px-2 py-1.5"><div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditFixed(f)}><Edit className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteFixed(f)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        {/* === REPORTES === */}
        <TabsContent value="reportes" className="space-y-4">
          <Card><CardContent className="p-3 space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-muted-foreground mr-1">Rango rápido:</span>
              <Button type="button" variant="outline" size="sm" onClick={() => setRangePreset('hoy')} data-testid="fin-range-hoy">Hoy</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setRangePreset('semana')} data-testid="fin-range-semana" title="Viernes a jueves">Semana (Vie–Jue)</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setRangePreset('mes')} data-testid="fin-range-mes">Mes</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setRangePreset('anio')} data-testid="fin-range-anio">Año</Button>
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="space-y-1"><Label className="text-xs">Fecha inicial</Label><Input type="date" value={reportFrom} max={reportTo || undefined} onChange={(e) => setReportFrom(e.target.value)} className="w-[160px]" data-testid="fin-report-from" /></div>
              <div className="space-y-1"><Label className="text-xs">Fecha final</Label><Input type="date" value={reportTo} min={reportFrom || undefined} onChange={(e) => setReportTo(e.target.value)} className="w-[160px]" data-testid="fin-report-to" /></div>
              <div className="space-y-1"><Label className="text-xs">Técnico</Label>
                <Select value={reportTec} onValueChange={setReportTec}><SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todos</SelectItem>{technicians.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </CardContent></Card>

          {report && (
            <>
              <p className="text-sm text-muted-foreground">Reporte del {report.from} al {report.to}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Órdenes</p><p className="text-2xl font-bold">{report.ordenes}</p></CardContent></Card>
                <Card className="bg-emerald-50/40 dark:bg-emerald-950/20"><CardContent className="p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" />Ventas</p><p className="text-2xl font-bold text-emerald-600">{fmt(report.ventas)}</p></CardContent></Card>
                <Card className="bg-red-50/40 dark:bg-red-950/20"><CardContent className="p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3 w-3" />Gastos orden</p><p className="text-2xl font-bold text-red-600">{fmt(report.gastos_orden)}</p></CardContent></Card>
                <Card className={`${report.utilidad_neta >= 0 ? 'bg-blue-50/40 dark:bg-blue-950/20' : 'bg-red-50/40 dark:bg-red-950/20'}`}><CardContent className="p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" />Utilidad neta</p><p className={`text-2xl font-bold ${report.utilidad_neta >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{fmt(report.utilidad_neta)}</p></CardContent></Card>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Gastos fijos (prorrateado)</p><p className="text-lg font-bold text-red-600">{fmt(report.gastos_fijos)}</p></CardContent></Card>
              </div>

              <Card><CardContent className="p-3">
                <h3 className="font-semibold mb-2 flex items-center gap-2"><Users className="h-4 w-4" />Rendimiento por técnico</h3>
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-muted/60"><tr className="text-xs">
                    <th className="text-left px-2 py-1.5">Técnico</th>
                    <th className="text-right px-2 py-1.5">Órdenes</th>
                    <th className="text-right px-2 py-1.5">Ventas</th>
                    <th className="text-right px-2 py-1.5">Gastos</th>
                    <th className="text-right px-2 py-1.5">Utilidad</th>
                  </tr></thead>
                  <tbody>
                    {Object.keys(report.by_tech).length === 0 ? <tr><td colSpan={5} className="text-center py-4 text-muted-foreground">Sin datos</td></tr> :
                      Object.entries(report.by_tech).sort((a, b) => b[1].utilidad - a[1].utilidad).map(([t, d]) => (
                        <tr key={t} className="border-t">
                          <td className="px-2 py-1.5">{t}</td>
                          <td className="px-2 py-1.5 text-right">{d.ordenes}</td>
                          <td className="px-2 py-1.5 text-right text-emerald-600">{fmt(d.ventas)}</td>
                          <td className="px-2 py-1.5 text-right text-red-600">{fmt(d.gastos)}</td>
                          <td className={`px-2 py-1.5 text-right font-bold ${d.utilidad >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{fmt(d.utilidad)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table></div>
              </CardContent></Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* === ORDER DIALOG === */}
      <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editOrder ? `Editar orden ${editOrder.folio}` : 'Nueva orden de trabajo'}</DialogTitle>
            <DialogDescription>Registro interno de Control Financiero.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitOrder} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Fecha</Label><Input type="date" value={orderForm.fecha} onChange={(e) => setOrderForm({...orderForm, fecha: e.target.value})} /></div>
              <div><Label className="text-xs">Tipo servicio</Label><Input value={orderForm.tipo_servicio} onChange={(e) => setOrderForm({...orderForm, tipo_servicio: e.target.value})} placeholder="Ej: Plomería" /></div>
              <div><Label className="text-xs">Estatus</Label>
                <Select value={orderForm.estatus} onValueChange={(v) => setOrderForm({...orderForm, estatus: v})}>
                  <SelectTrigger data-testid="fin-order-estatus"><SelectValue /></SelectTrigger>
                  <SelectContent>{ESTATUS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <fieldset className="rounded-md border p-3"><legend className="text-xs font-semibold px-1">Cliente</legend>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Nombre *</Label><Input value={orderForm.cliente_nombre} onChange={(e) => setOrderForm({...orderForm, cliente_nombre: e.target.value})} required /></div>
                <div><Label className="text-xs">Teléfono</Label><Input value={orderForm.cliente_telefono} onChange={(e) => setOrderForm({...orderForm, cliente_telefono: e.target.value})} /></div>
              </div>
              <div className="mt-2"><Label className="text-xs">Domicilio</Label><Input value={orderForm.cliente_direccion} onChange={(e) => setOrderForm({...orderForm, cliente_direccion: e.target.value})} /></div>
            </fieldset>
            <div><Label className="text-xs">Técnico asignado</Label>
              <select className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm" value={orderForm.tecnico_id} onChange={(e) => setOrderTec(e.target.value)}>
                <option value="">—</option>
                {technicians.map(t => <option key={t.id} value={t.id}>{t.name} {t.tipo === 'ayudante' ? '(Ayudante)' : ''}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Trabajo realizado *</Label><Textarea value={orderForm.trabajo} onChange={(e) => setOrderForm({...orderForm, trabajo: e.target.value})} rows={2} required /></div>
            <fieldset className="rounded-md border bg-emerald-50/40 dark:bg-emerald-950/20 p-3"><legend className="text-xs font-semibold px-1">💰 Cobranza</legend>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Total cobrado</Label><Input type="number" value={orderForm.total_cobrado} onChange={(e) => setOrderForm({...orderForm, total_cobrado: e.target.value})} /></div>
                <div><Label className="text-xs">Anticipo</Label><Input type="number" value={orderForm.anticipo} onChange={(e) => setOrderForm({...orderForm, anticipo: e.target.value})} /></div>
                <div><Label className="text-xs">Saldo restante</Label><Input value={fmt(c.saldo)} disabled className="bg-muted" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label className="text-xs">Folio de nota {orderForm.estatus === 'finalizada' && <span className="text-red-500">*</span>}</Label><Input value={orderForm.folio_nota} onChange={(e) => setOrderForm({...orderForm, folio_nota: e.target.value})} placeholder="Ej: N-12345" data-testid="fin-folio-nota" required={orderForm.estatus === 'finalizada'} /></div>
                <div><Label className="text-xs">Forma de pago</Label>
                  <Select value={orderForm.forma_pago || ''} onValueChange={(v) => setOrderForm({...orderForm, forma_pago: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </fieldset>
            <fieldset className="rounded-md border bg-red-50/40 dark:bg-red-950/20 p-3"><legend className="text-xs font-semibold px-1">📉 Gastos</legend>
              <div><Label className="text-xs">Material utilizado</Label><Textarea value={orderForm.material_utilizado} onChange={(e) => setOrderForm({...orderForm, material_utilizado: e.target.value})} rows={2} placeholder="Ej: 2 codos CPVC, 1m tubo..." /></div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><Label className="text-xs">Gastos material</Label><Input type="number" value={orderForm.gastos_material} onChange={(e) => setOrderForm({...orderForm, gastos_material: e.target.value})} /></div>
                <div><Label className="text-xs">Gasolina</Label><Input type="number" value={orderForm.gasolina} onChange={(e) => setOrderForm({...orderForm, gasolina: e.target.value})} /></div>
                <div><Label className="text-xs">Otros gastos</Label><Input type="number" value={orderForm.otros_gastos} onChange={(e) => setOrderForm({...orderForm, otros_gastos: e.target.value})} /></div>
              </div>
            </fieldset>
            <div className="rounded-md border bg-blue-50/40 dark:bg-blue-950/20 p-3 grid grid-cols-3 gap-2 text-sm">
              <div><p className="text-xs text-muted-foreground">Total gastos</p><p className="font-bold text-red-600">{fmt(c.gastos)}</p></div>
              <div><p className="text-xs text-muted-foreground">Saldo restante</p><p className="font-bold">{fmt(c.saldo)}</p></div>
              <div><p className="text-xs text-muted-foreground">Utilidad neta</p><p className={`font-bold ${c.utilidad >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(c.utilidad)}</p></div>
            </div>
            <div><Label className="text-xs">Observaciones</Label><Textarea value={orderForm.observaciones} onChange={(e) => setOrderForm({...orderForm, observaciones: e.target.value})} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOrderOpen(false)}>Cancelar</Button><Button type="submit" data-testid="fin-order-submit">{editOrder ? 'Guardar' : 'Crear orden'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* === FIXED DIALOG === */}
      <Dialog open={fixedOpen} onOpenChange={setFixedOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editFixed ? 'Editar gasto fijo' : 'Nuevo gasto fijo'}</DialogTitle></DialogHeader>
          <form onSubmit={submitFixed} className="space-y-3">
            <div><Label className="text-xs">Concepto *</Label><Input value={fixedForm.concepto} onChange={(e) => setFixedForm({...fixedForm, concepto: e.target.value})} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Categoría</Label>
                <Select value={fixedForm.categoria} onValueChange={(v) => setFixedForm({...fixedForm, categoria: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIAS_GASTO.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Monto mensual</Label><Input type="number" value={fixedForm.monto_mensual} onChange={(e) => setFixedForm({...fixedForm, monto_mensual: e.target.value})} required /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Desde</Label><Input type="date" value={fixedForm.fecha_inicio} onChange={(e) => setFixedForm({...fixedForm, fecha_inicio: e.target.value})} /></div>
              <div className="flex items-end gap-2"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={fixedForm.activo} onChange={(e) => setFixedForm({...fixedForm, activo: e.target.checked})} />Activo</label></div>
            </div>
            <div><Label className="text-xs">Observaciones</Label><Textarea value={fixedForm.observaciones} onChange={(e) => setFixedForm({...fixedForm, observaciones: e.target.value})} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setFixedOpen(false)}>Cancelar</Button><Button type="submit">Guardar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
